import { useEffect, useState } from 'react';
import { Editor, EditorContainer } from 'src/components/ui/editor/editor';
import { Plate, usePlateEditor } from 'platejs/react';
import type { AnyPluginConfig, Value } from 'platejs';
import { FixedToolbarKit } from 'src/components/ui/editor/plugins/fixed-toolbar-kit';
import { LinkKit } from 'src/components/ui/editor/plugins/link-kit';
import { ListKit } from 'src/components/ui/editor/plugins/list-kit';
import { CodeBlockKit } from 'src/components/ui/editor/plugins/code-block-kit';
import { BasicBlocksKit } from 'src/components/ui/editor/plugins/basic-blocks-kit';
import { BasicMarksKit } from 'src/components/ui/editor/plugins/basic-marks-kit';
import { EmojiKit } from 'src/components/ui/editor/plugins/emoji-kit';
import { LineHeightKit } from 'src/components/ui/editor/plugins/line-height-kit';
import { BlockPlaceholderKit } from 'src/components/ui/editor/plugins/block-placeholder-kit';
import { MarkdownKit } from 'src/components/ui/editor/plugins/markdown-kit';
import { TableKit } from 'src/components/ui/editor/plugins/table-kit';
import { MediaKit } from 'src/components/ui/editor/plugins/media-kit';
import { clsx } from 'clsx';
import { FieldError } from 'react-hook-form';
import { MarkdownPlugin } from '@platejs/markdown';
import { InvalidMessage } from 'src/components/Input/InvalidMessage';

const INITIAL_EDITOR_VALUE: Value = [
  {
    children: [{ text: '' }],
    type: 'p',
  },
];

type PlateEditorProps = {
  initialValue?: string;
  resetOnReadOnlyChange?: boolean;
  label?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onClick?: () => void;
  error?: null | FieldError;
  containerClassName?: string;
  readOnly?: boolean;
  className?: string;
};

export const PlateEditor = ({
  label = 'Content/Answer',
  initialValue = '',
  onChange = () => {},
  onFocus = () => {},
  onClick = () => {},
  error = null,
  className = '',
  containerClassName = '',
  readOnly = false,
  resetOnReadOnlyChange = false,
}: PlateEditorProps) => {
  const [errorState, setErrorState] = useState<Error | null>(null);
  // Explicit type arguments: with strictNullChecks off, the hook's default `enabled` type
  // would otherwise resolve its return type to null.
  const editor = usePlateEditor<Value, AnyPluginConfig, true>({
    plugins: [
      ...BasicBlocksKit,
      ...BasicMarksKit,
      ...FixedToolbarKit,
      ...LinkKit,
      ...ListKit,
      ...CodeBlockKit,
      ...EmojiKit,
      ...LineHeightKit,
      ...BlockPlaceholderKit,
      ...MarkdownKit,
      ...TableKit,
      ...MediaKit,
    ], // Add the mark plugins
    value: INITIAL_EDITOR_VALUE,
  }); // Initializes the editor instance

  const setInitialValue = () => {
    try {
      const result = editor.getApi(MarkdownPlugin).markdown.deserialize(initialValue);

      editor.tf.setValue(result);
      setErrorState(null);
    } catch (e) {
      setErrorState(e);
    }
  };

  useEffect(() => {
    setInitialValue();
  }, [initialValue]);

  useEffect(() => {
    if (resetOnReadOnlyChange) {
      setInitialValue();
    }
  }, [readOnly]);

  return (
    <div className={'relative w-full h-full'}>
      <label className={clsx(error && '!text-[#FF5151]')}>{label}</label>
      {error && (
        <InvalidMessage
          className={
            'backdrop-blur-0 z-[60] !absolute top-7 right-2 px-4 py-1.5 border border-[#FF5151]'
          }
          closeIconVisible={false}
          invalidMessage={error.message}
        />
      )}
      {errorState ? (
        <p className={'text-center text-base text-ebony font-medium'}>
          Cannot open file
          <br /> Error: {errorState.message}, etc.
        </p>
      ) : (
        <Plate
          editor={editor}
          readOnly={readOnly}
          onChange={({ editor }) => {
            const valueSerialized: string = editor.getApi(MarkdownPlugin).markdown.serialize();

            onChange?.(valueSerialized);
          }}
        >
          <EditorContainer
            onClick={onClick}
            className={clsx(
              error &&
                'border !border-[#FF5151] [&_.toolbar]:border-[#FF5151] [&_.toolbar]:bg-[#ff51513b] [&_.separator]:bg-[#FF5151]',
              readOnly && '[&_.toolbar]:hidden',
              containerClassName,
            )}
          >
            <Editor
              onFocus={() => {
                onFocus?.();
              }}
              variant={'inpopup'}
              className={className}
              placeholder="Type something..."
            />
          </EditorContainer>
        </Plate>
      )}
    </div>
  );
};
